import { KeyFacet, stringPart, subKeyPart, Key } from '../models/KeyFacet';
import { AutoKeyFacet } from '../models/AutoKeyFacet';
import { resolveItems, supportFacets } from './getItems';
import { IAsyncActionCreator, createAsyncActionCreator } from '../store/actions/createActionCreator';
import { IItemCacheThunkAction, IItemCacheDispatch } from '../store/actions/ItemCacheActions';
import { IItemCache } from '../models/IItemCacheObserver';
import { Facet, pack, IFacet } from '../models/Facet';
import { Delete, ArrayConcatIfNotMatchKeyPatch, ArrayRemoveMatchPatch } from '../utilities/Graft';
import { IOperationPayload, startOperation, IOperationActionCreator, IOperationHandler } from '../store/actions/implementOperation';
import {
    progressPhaseFacet,
    ProgressPhase,
    progressSizeFacet,
    Histogram,
    sourceItemFacet,
    cancelSourceFacet,
    overallProgressCountsFacet,
    DEFAULT_PROGRESS_COUNTS,
    overallProgressSizeFacet,
    overallProgressPhaseFacet,
    DEFAULT_PROGRESS_PHASE,
    ISimpleProgressBreakdown,
    overallSimpleProgressBreakdownFacet,
    DEFAULT_SIMPLE_PROGRESS_BREAKDOWN
} from '../facets/ProgressFacets';
import { childItemsFacet } from '../facets/SetFacets';
import { isGroupFacet } from '../facets/GroupFacets';
import { errorFacet } from '../facets/DemandFacets';
import { readItems } from './readItems';
import { isCancel, CancelSource } from '../utilities/async/CancelError';
import { cancelProgressItemsOperation } from './progress/cancelProgressItems';
import { IBatchOperationPayload, createBatchedItemsOperationHandler, createAddon, IAddon } from '../addons/registerOperationHandler';
import { ItemResolver } from '../models/items/ItemResolver';
import { registerItemDemands, removeItemDemands, registerPermanentItemDemands } from './pruneItems';

/**
 * Kery facet which owns the progress root item.
 * @public
 */
export const progressRootItemKeyFacet: KeyFacet<{}> = new KeyFacet('progressRoot', {});

/**
 * The key for the progress root item.
 * Components which wish to report progress must attach their progress items
 * as children to the single progress root item in the cache.
 * @public
 */
export const progressRootItemKey = progressRootItemKeyFacet.serialize({});

/**
 * @public
 */
export interface IExpireProgressItemsPayload {
    items: {
        [itemKey: string]: boolean;
    };
}

export const progressAddon: IAddon = createAddon(() => {
    return (dispatch: IItemCacheDispatch) => {
        dispatch(registerPermanentItemDemands({
            items: {
                [progressRootItemKey]: {}
            }
        }));

        dispatch(supportFacets({
            keyFacet: progressRootItemKeyFacet,
            facets: []
        }));
    };
});

/**
 * Creates an action which, when dispatched, marks the specified progress items as expired.
 * @public
 */
export const expireProgressItems: IAsyncActionCreator<IExpireProgressItemsPayload, IItemCacheThunkAction<void>> =
    createAsyncActionCreator((payload: IExpireProgressItemsPayload) => {
        const itemKeys = Object.keys(payload.items);

        return (dispatch: IItemCacheDispatch) => {
            const result = dispatch(readItems((itemCache: IItemCache) => {
                const itemResolver = new ItemResolver();

                const keys: { [itemKey: string]: boolean; } = {};

                if (itemKeys) {
                    for (const itemKey of itemKeys) {
                        const phase = getOverallProgressPhase(itemCache, { itemKey });

                        if (phase === ProgressPhase.canceled ||
                            phase === ProgressPhase.completed ||
                            phase === ProgressPhase.failed) {
                            keys[itemKey] = true;

                            itemResolver.resolveItems({
                                items: {
                                    [itemKey]: {
                                        ...progressPhaseFacet.pack(ProgressPhase.expired)
                                    }
                                }
                            });
                        }
                    }
                }

                itemResolver.resolveItems({
                    items: {
                        [progressRootItemKey]: pack({
                            childItems: childItemsFacet
                        }, {
                                childItems: {
                                    itemKeys: new ArrayRemoveMatchPatch((key: string) => !!keys[key])
                                }
                            })
                    }
                });

                return {
                    itemResolver
                };
            }));

            return dispatch(result.itemResolver.execute());
        };
    });

const blockExpirationFacet = new Facet<{ [key: string]: boolean; }>('blockExpiration');
const blockExpirationOwnerKeyFacet = new AutoKeyFacet('blockExpirationOwner');

/**
 * Creates an action which, when dispatched, blocks expiration of progress items until a subsequent action
 * is created and dispatched.
 *
 * This action creator produces as action which returns a new action creator once dispatched.
 *
 * @deprecated This operation and related components are no longer used.
 * @internal
 */
export const blockProgressExpiration: IAsyncActionCreator<{}, IItemCacheThunkAction<IAsyncActionCreator<{}, IItemCacheThunkAction<void>>>> =
    createAsyncActionCreator((payload: {}) => {
        const blockExpirationOwnerKey = blockExpirationOwnerKeyFacet.serializeNext();

        return (dispatch: IItemCacheDispatch) => {
            dispatch(resolveItems({
                items: {
                    [progressRootItemKey]: {
                        ...blockExpirationFacet.pack({
                            [blockExpirationOwnerKey]: true
                        })
                    }
                }
            }));

            return createAsyncActionCreator((payload2: {}) => {
                return (dispatch2: IItemCacheDispatch) => {
                    dispatch(resolveItems({
                        items: {
                            [progressRootItemKey]: pack({
                                blockExpiration: blockExpirationFacet
                            }, {
                                    blockExpiration: {
                                        [blockExpirationOwnerKey]: new Delete<boolean>()
                                    }
                                })
                        }
                    }));
                };
            });
        };
    });

/**
 * Determines whether or not progress expiration is currently blocked by an active UX component.
 * @deprecated This function and its related components are no longer used.
 * @internal
 */
export function isProgressExpirationBlocked(itemCache: IItemCache, props: {}): boolean {
    const blockExpiration = itemCache.demandItemFacet(blockExpirationFacet, progressRootItemKey);

    return !!blockExpiration && Object.keys(blockExpiration).length > 0;
}

/**
 * @public
 */
export function getTotalProgressSize(itemCache: IItemCache, props: {
    itemKey: string | undefined;
}): typeof progressSizeFacet.type | undefined {
    return itemCache.demandItemFacet(overallProgressSizeFacet, props.itemKey);
}

/**
 * @public
 */
export function getOverallProgressPhase(itemCache: IItemCache, props: {
    itemKey: string | undefined;
}): ProgressPhase {
    return itemCache.demandItemFacet(overallProgressPhaseFacet, props.itemKey) || DEFAULT_PROGRESS_PHASE;
}

/**
 * Gets the counts of items per progress phase, recursively from the root upload progress item.
 * @public
 */
export function getItemCountsByProgressPhase(itemCache: IItemCache, props: {
    itemKey: string | undefined;
}): Histogram<ProgressPhase> {
    return itemCache.demandItemFacet(overallProgressCountsFacet, props.itemKey) || DEFAULT_PROGRESS_COUNTS;
}

/**
 * @public
 */
export function getOverallSimpleProgressBreakdown(itemCache: IItemCache, props: {
    itemKey: string | undefined;
}): ISimpleProgressBreakdown {
    return itemCache.demandItemFacet(overallSimpleProgressBreakdownFacet, props.itemKey) || DEFAULT_SIMPLE_PROGRESS_BREAKDOWN;
}

/**
 * @public
 */
export const progressBatchKeyFacet = new AutoKeyFacet('progressBatch');

/**
 * @public
 */
export class ProgressKeyFacet<TOptions> extends KeyFacet<{
    taskKey: string | undefined;
    batch: typeof progressBatchKeyFacet.type;
}> {
    private _getTaskKey?: (itemKey: string, options: TOptions) => string;

    constructor(name: string, getTaskKey?: (itemKey: string, options: TOptions) => string) {
        super(`progress-${name}`, {
            taskKey: stringPart(),
            batch: subKeyPart(progressBatchKeyFacet)
        });

        this._getTaskKey = getTaskKey;
    }

    public serializeBatchKey(batchKey: Key<typeof progressBatchKeyFacet.type>): string {
        return this.serialize({
            batch: progressBatchKeyFacet.deserialize(batchKey),
            taskKey: undefined
        });
    }

    public serializeTaskKey(batchKey: Key<typeof progressBatchKeyFacet.type>, itemKey: string, options: TOptions): string {
        const getTaskKey = this._getTaskKey;

        return this.serialize({
            batch: progressBatchKeyFacet.deserialize(batchKey),
            taskKey: getTaskKey ? getTaskKey(itemKey, options) : itemKey
        });
    }
}

/**
 * @public
 */
export interface IInitializeProgressBatchPayload<TOptions> extends IOperationPayload<TOptions> {
    progressBatchKey: string;
}

/**
 * Helper which creates an action creator to implement an 'operation' to be executed by an addon
 * registered in the store.
 *
 * This function takes some configuration arguments and produces a function which can be invoked by
 * UX Actions to execute an operation with progress reporting.
 *
 * Use this function to implement most operations which require 'fire-and-forget' progress reporting.
 * @public
 */
export function implementOperationWithProgress<TOptions extends object>(options: {
    /**
     * The operation type facet, which determines the options to pass for each
     * item, and which queue is used for processing.
     */
    operationTypeFacet: IFacet<TOptions>;
    progressKeyFacet: ProgressKeyFacet<TOptions>;
    /**
     * An addon to dispatch which will be used to register handlers for progress reporting.
     */
    initializeBatch?: (payload: IInitializeProgressBatchPayload<TOptions>) => IItemCacheThunkAction<void>;
}): IOperationActionCreator<TOptions> {
    const {
        operationTypeFacet,
        progressKeyFacet,
        initializeBatch
    } = options;

    return createAsyncActionCreator((payload: IOperationPayload<TOptions>) => {
        const {
            items
        } = payload;

        return async (dispatch: IItemCacheDispatch) => {
            const batchKey = progressBatchKeyFacet.serializeNext();

            const progressBatchItemKey = progressKeyFacet.serializeBatchKey(batchKey);

            const owner = {};

            dispatch(registerItemDemands({
                owner,
                items: {
                    [progressBatchItemKey]: {}
                }
            }));

            const progressItemKeys: string[] = [];

            const itemResolver = new ItemResolver();

            for (const itemKey in items) {
                if (Object.prototype.hasOwnProperty.call(items, itemKey)) {
                    const itemOptions = items[itemKey];

                    const progressItemKey = progressKeyFacet.serializeTaskKey(batchKey, itemKey, itemOptions);

                    itemResolver.resolveItems({
                        items: {
                            [progressItemKey]: {
                                ...sourceItemFacet.pack({
                                    itemKey: itemKey
                                }),
                                ...isGroupFacet.pack(true),
                                ...pack({
                                    options: operationTypeFacet
                                }, {
                                        options: itemOptions
                                    }),
                                ...progressPhaseFacet.pack(ProgressPhase.created)
                            }
                        }
                    });

                    progressItemKeys.push(progressItemKey);
                }
            }

            itemResolver.resolveItems({
                items: {
                    [progressRootItemKey]: pack({
                        childItems: childItemsFacet
                    }, {
                            childItems: {
                                itemKeys: new ArrayConcatIfNotMatchKeyPatch([progressBatchItemKey], (i: string) => i)
                            }
                        }),
                    [progressBatchItemKey]: pack({
                        childItems: childItemsFacet
                    }, {
                            childItems: {
                                itemKeys: new ArrayConcatIfNotMatchKeyPatch(progressItemKeys, (i: string) => i)
                            }
                        })
                }
            });

            dispatch(itemResolver.execute());

            if (initializeBatch) {
                dispatch(initializeBatch({
                    ...payload,
                    progressBatchKey: progressBatchItemKey
                }));
            }

            const {
                completed,
                items: workByItem
            } = dispatch(startOperation({
                operationTypeFacet: operationTypeFacet,
                items: items
            }));

            for (const itemKey in workByItem) {
                if (Object.prototype.hasOwnProperty.call(workByItem, itemKey)) {
                    const itemOptions = items[itemKey];

                    const progressItemKey = progressKeyFacet.serializeTaskKey(batchKey, itemKey, itemOptions);

                    dispatch(trackItemProgress({
                        progressItemKey: progressItemKey,
                        ...workByItem[itemKey]
                    }));
                }
            }

            try {
                await completed;
            } finally {
                dispatch(removeItemDemands({
                    owner
                }));
            }
        };
    });
}

/**
 * Creates an action which, when dispatched, tracks the status of an operation
 * and updates a progress item with the outcome.
 * @public
 */
export function trackItemProgress(payload: {
    progressItemKey: string;
    started: Promise<void>;
    completed: Promise<void>;
}): IItemCacheThunkAction<Promise<void>> {
    const {
        progressItemKey,
        started,
        completed
    } = payload;

    return async (dispatch: IItemCacheDispatch) => {
        const owner = {};

        dispatch(registerItemDemands({
            owner,
            items: {
                [progressItemKey]: {}
            }
        }));

        try {
            await started;

            dispatch(resolveItems({
                items: {
                    [progressItemKey]: {
                        ...progressPhaseFacet.pack(ProgressPhase.started)
                    }
                }
            }));

            await completed;

            dispatch(resolveItems({
                items: {
                    [progressItemKey]: {
                        ...progressPhaseFacet.pack(ProgressPhase.completed)
                    }
                }
            }));
        } catch (e) {
            dispatch(resolveItems({
                items: {
                    [progressItemKey]: {
                        ...progressPhaseFacet.pack(isCancel(e) ?
                            ProgressPhase.canceled :
                            ProgressPhase.failed),
                        ...(e ? {
                            ...errorFacet.pack(e)
                        } : {})
                    }
                }
            }));

            // Do not rethrow, since updating the progress item will suffice to report the error.
        } finally {
            dispatch(removeItemDemands({
                owner
            }));
        }
    };
}

/**
 * Creates an operation handler for canceling progress items of a given type
 * using a default system.
 * @beta
 */
export function createCancelProgressItemsOperationHandler<TKey extends object>(
    progressKeyFacet: KeyFacet<TKey>
): IOperationHandler<TKey, typeof cancelProgressItemsOperation.type> {
    return createBatchedItemsOperationHandler()(cancelProgressItems);

    function cancelProgressItems(
        payload: IBatchOperationPayload<typeof progressKeyFacet.type, typeof cancelProgressItemsOperation.type>
    ): IItemCacheThunkAction<Promise<void>> {
        return async (dispatch: IItemCacheDispatch) => {
            const result = dispatch(readItems((itemCache: IItemCache) => {
                const itemResolver = new ItemResolver();

                const sourcesToCancel: CancelSource[] = [];

                function markItems(itemKey: string): void {
                    const cancelSource = itemCache.demandItemFacet(cancelSourceFacet, itemKey);

                    if (cancelSource) {
                        sourcesToCancel.push(cancelSource);
                    }

                    itemResolver.resolveItems({
                        items: {
                            [itemKey]: {
                                ...(!cancelSource ? {
                                    ...progressPhaseFacet.pack(ProgressPhase.canceled)
                                } : {}),
                                ...pack({
                                    error: errorFacet
                                }, {
                                        error: new Delete<Error>()
                                    })
                            }
                        }
                    });

                    const childItems = itemCache.demandItemFacet(childItemsFacet, itemKey);

                    if (childItems) {
                        for (const childItemKey of childItems.itemKeys) {
                            markItems(childItemKey);
                        }
                    }
                }

                for (const itemKey in payload.items) {
                    if (Object.prototype.hasOwnProperty.call(payload.items, itemKey)) {
                        markItems(itemKey);
                    }
                }

                return {
                    itemResolver,
                    sourcesToCancel
                };
            }));

            dispatch(result.itemResolver.execute());

            for (const cancelSource of result.sourcesToCancel) {
                cancelSource.cancel();
            }
        };
    }
}
